import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API_BASE } from "@/App";
import {
  BookOpen, Target, TrendingUp, BarChart3,
  ArrowLeft, ChevronRight, GraduationCap,
  MapPin, FileText, Building2, Landmark, Users, Brain,
  Dna, Flame, Newspaper, AlertCircle, CheckCircle2, Play,
  BookMarked, Lock, Trophy
} from "lucide-react";

/**
 * TGPSC Prelims Hub - Telangana Group-1 Prelims Center
 * Mirrors UPSC Prelims Hub structure with TGPSC-specific content
 */

const TGPSCPrelimsHubPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPro = user?.role === "PRO";

  // TGPSC Prelims: July 15, 2026
  const TGPSC_DATE = new Date('2026-07-15');
  const getDaysRemaining = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const exam = new Date(TGPSC_DATE);
    exam.setHours(0, 0, 0, 0);
    return Math.ceil((exam - today) / (1000 * 60 * 60 * 24));
  };
  const daysToExam = getDaysRemaining();
  const weeksToExam = Math.floor(daysToExam / 7);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(`${API_BASE}/prelims/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureClick = (path) => {
    navigate(path);
  };

  // Hero features
  const heroFeatures = [
    { 
      title: "Daily Challenge", 
      description: "10 fresh AI-generated MCQs every day", 
      icon: Target, 
      path: "/daily-challenge", 
      color: "from-rose-500 to-pink-600",
      badge: "New Today!"
    },
    { 
      title: "MCQ Practice", 
      description: "Topic-wise & random practice with feedback", 
      icon: Target, 
      path: "/tgpsc/mcq", 
      color: "from-indigo-500 to-blue-600",
      badge: `${stats?.total_mcqs_attempted || 0} attempted`
    },
  ];

  // Practice & Tests
  const practiceFeatures = [
    { title: "Test Series", description: "Full-length mock tests with analysis", icon: FileText, path: "/prelims/test-series", color: "from-purple-500 to-pink-500" },
    { title: "PYQ Papers", description: "Year-wise previous papers (2015-2024)", icon: BookMarked, path: "/tgpsc/pyq", color: "from-amber-500 to-orange-500" },
    { title: "Mistake Tracker", description: "Track & revise wrong answers", icon: AlertCircle, path: "/mistake-tracker", color: "from-orange-500 to-red-500" },
  ];

  // Analytics & Insights
  const analyticsFeatures = [
    { title: "PYQ Pattern Genome", description: "Topic rotation tracker - predict 2026 questions", icon: Dna, path: "/tgpsc/genome", color: "from-purple-500 to-pink-500", highlight: true },
    { title: "Topic Heatmap", description: "High-probability topics for all subjects", icon: BarChart3, path: "/tgpsc/heatmap", color: "from-blue-500 to-indigo-500" },
    { title: "PYQ Analysis", description: "10-year trend analysis & predictions", icon: TrendingUp, path: "/tgpsc/pyq", color: "from-emerald-500 to-green-500" },
    { title: "Weak Area Analytics", description: "Identify gaps & improvement plan", icon: AlertCircle, path: "/prelims/analytics", color: "from-orange-500 to-red-500", proOnly: true },
  ];

  // Telangana-Specific Subjects
  const stateSubjects = [
    { id: "telangana_history", name: "Telangana History", icon: Landmark, color: "from-pink-500 to-rose-600" },
    { id: "telangana_geography", name: "Telangana Geography", icon: MapPin, color: "from-emerald-500 to-teal-600" },
    { id: "telangana_movement", name: "Telangana Movement", icon: Users, color: "from-orange-500 to-amber-600" },
    { id: "telangana_economy", name: "Telangana Economy", icon: Building2, color: "from-blue-500 to-indigo-600" },
  ];

  // General Studies Subjects
  const generalSubjects = [
    { id: "polity", name: "Indian Polity", icon: GraduationCap, color: "from-purple-500 to-violet-600" },
    { id: "economy", name: "Indian Economy", icon: BarChart3, color: "from-cyan-500 to-blue-600" },
    { id: "geography", name: "Indian Geography", icon: MapPin, color: "from-green-500 to-emerald-600" },
    { id: "history", name: "Indian History", icon: BookOpen, color: "from-amber-500 to-orange-600" },
    { id: "science", name: "Science & Technology", icon: Brain, color: "from-sky-500 to-blue-600" },
    { id: "current_affairs", name: "Current Affairs", icon: Newspaper, color: "from-red-500 to-rose-600" },
  ];

  const getStatusColor = (status) => {
    if (status === "safe") return "from-emerald-500 to-green-500";
    if (status === "borderline") return "from-amber-500 to-orange-500";
    return "from-red-500 to-rose-500";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-pink-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-pink-300">Loading TGPSC Prelims Hub...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-rose-50/30 to-pink-50/30 pb-20">
      
      {/* Header with Stats */}
      <div className="bg-gradient-to-r from-pink-600 via-rose-600 to-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate("/tgpsc")}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Target className="w-7 h-7" />
                  TGPSC Prelims Hub
                </h1>
                <p className="text-pink-200 text-sm">Telangana Group-1 Prelims</p>
              </div>
            </div>
            
            {/* Days Counter */}
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2">
              <p className="text-2xl font-bold leading-none" data-testid="days-counter">{daysToExam}</p>
              <p className="text-[10px] text-pink-200">days to Prelims</p>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{stats?.total_mcqs_attempted || 0}</p>
              <p className="text-[10px] text-pink-200">MCQs Done</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{stats?.overall_accuracy || 0}%</p>
              <p className="text-[10px] text-pink-200">Accuracy</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{stats?.tests_taken || 0}</p>
              <p className="text-[10px] text-pink-200">Tests</p>
            </div>
            <div className="bg-gradient-to-br from-amber-500/30 to-orange-500/30 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{weeksToExam}</p>
              <p className="text-[10px] text-amber-200">Weeks Left</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        
        {/* Status Banner */}
        <Card className={`border-0 shadow-lg overflow-hidden bg-gradient-to-r ${getStatusColor(stats?.gs_status || 'needs_attention')}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  {stats?.gs_status === "safe" ? (
                    <CheckCircle2 className="w-7 h-7" />
                  ) : (
                    <AlertCircle className="w-7 h-7" />
                  )}
                </div>
                <div>
                  <p className="text-white/80 text-xs uppercase tracking-wide">Prelims Status</p>
                  <p className="text-2xl font-bold" data-testid="status-text">
                    {stats?.gs_status === "safe" ? "SAFE ZONE" :
                     stats?.gs_status === "borderline" ? "BORDERLINE" :
                     "NEEDS ATTENTION"}
                  </p>
                  <p className="text-white/80 text-sm">{stats?.gs_accuracy || 0}% current accuracy</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate("/tgpsc/mcq")}
                className="bg-white text-slate-800 hover:bg-slate-100 font-semibold"
                data-testid="practice-now-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Practice Now
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Hero Features */}
        <div className="grid grid-cols-2 gap-4">
          {heroFeatures.map((feature, idx) => (
            <Card 
              key={idx}
              className={`border-0 shadow-xl cursor-pointer hover:scale-[1.02] transition-all overflow-hidden bg-gradient-to-br ${feature.color}`}
              onClick={() => handleFeatureClick(feature.path)}
              data-testid={`hero-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-5 text-white">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-3">
                  <feature.icon className="w-7 h-7" />
                </div>
                <h3 className="font-bold text-lg mb-1">{feature.title}</h3>
                <p className="text-white/80 text-sm mb-3">{feature.description}</p>
                <div className="flex items-center justify-between">
                  <Badge className="bg-white/20 text-white border-0 text-xs">{feature.badge}</Badge>
                  <ChevronRight className="w-5 h-5 text-white/60" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Practice & Tests */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Flame className="w-5 h-5 text-orange-500" />
            <h2 className="font-bold text-lg text-slate-800">Practice & Tests</h2>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {practiceFeatures.map((feature, idx) => (
              <Card 
                key={idx}
                className="border border-slate-200 bg-white cursor-pointer hover:shadow-lg hover:border-transparent transition-all group"
                onClick={() => handleFeatureClick(feature.path)}
                data-testid={`practice-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-md`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-semibold text-sm text-slate-800">{feature.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Analytics & Insights */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-5 h-5 text-indigo-500" />
            <h2 className="font-bold text-lg text-slate-800">Analytics & Insights</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {analyticsFeatures.map((feature, idx) => (
              <Card 
                key={idx}
                className={`border cursor-pointer hover:shadow-lg hover:border-transparent transition-all group ${
                  feature.highlight 
                    ? "border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50 ring-2 ring-purple-200" 
                    : "border-slate-200 bg-white"
                }`}
                onClick={() => handleFeatureClick(feature.path)}
                data-testid={`analytics-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-4 text-center relative">
                  {feature.highlight && (
                    <Badge className="absolute top-2 right-2 bg-purple-600 text-white border-0 text-[9px] px-1.5 py-0.5">NEW</Badge>
                  )}
                  <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform shadow-md`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-medium text-sm text-slate-800">{feature.title}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{feature.description}</p>
                  {feature.proOnly && !isPro && (
                    <Badge className="mt-2 bg-amber-100 text-amber-700 border-0 text-[10px]">
                      <Lock className="w-3 h-3 mr-1" /> PRO
                    </Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Telangana-Specific Subjects */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Landmark className="w-5 h-5 text-pink-500" />
            <h2 className="font-bold text-lg text-slate-800">Telangana-Specific</h2>
            <Badge className="bg-pink-100 text-pink-700 border-0 text-[10px]">State Focus</Badge>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {stateSubjects.map((subject) => (
              <Card
                key={subject.id}
                onClick={() => navigate(`/tgpsc/mcq?subject=${subject.id}`)}
                className="border border-slate-200 bg-white cursor-pointer hover:shadow-lg hover:border-pink-300 transition-all group"
                data-testid={`subject-${subject.id}`}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 bg-gradient-to-br ${subject.color} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm`}>
                    <subject.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-medium text-slate-800">{subject.name}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* General Studies Subjects */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-blue-500" />
            <h2 className="font-bold text-lg text-slate-800">General Studies</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {generalSubjects.map((subject) => (
              <Card
                key={subject.id}
                onClick={() => navigate(`/tgpsc/mcq?subject=${subject.id}`)}
                className="border border-slate-200 bg-white cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all group"
                data-testid={`subject-${subject.id}`}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 bg-gradient-to-br ${subject.color} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm`}>
                    <subject.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-medium text-slate-800">{subject.name}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* PRO Upgrade CTA */}
        {!isPro && (
          <Card 
            className="border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-indigo-50 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate("/upgrade")}
            data-testid="pro-upgrade-cta"
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-lg text-slate-800">Upgrade to CLARIAS PRO</p>
                  <p className="text-sm text-slate-600">Unlock all TGPSC features & analytics</p>
                </div>
                <ChevronRight className="w-6 h-6 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        )}

        <div className="h-8" />
      </div>
    </div>
  );
};

export default TGPSCPrelimsHubPage;
